/*
 * ALOHA QUEEN SUPERNOVA - Realistic Living Galaxy
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL - NO UNAUTHORIZED USE
 */

import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

const RealisticGalaxyBackground = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 3000);
    camera.position.set(0, 120, 250);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    mountRef.current.appendChild(renderer.domElement);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    scene.add(ambientLight);

    const sunLight = new THREE.PointLight(0xffffff, 3, 800);
    sunLight.position.set(0, 0, 0);
    scene.add(sunLight);

    // REALISTIC SUN with corona
    const sunGeometry = new THREE.SphereGeometry(10, 64, 64);
    const sunMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFA500,
      emissive: 0xFF6600,
      emissiveIntensity: 1.5
    });
    const sun = new THREE.Mesh(sunGeometry, sunMaterial);
    scene.add(sun);

    // Sun corona layers
    for (let i = 1; i <= 3; i++) {
      const coronaGeo = new THREE.SphereGeometry(10 + i * 2, 32, 32);
      const coronaMat = new THREE.MeshBasicMaterial({
        color: i === 1 ? 0xFFAA00 : i === 2 ? 0xFF8800 : 0xFF6600,
        transparent: true,
        opacity: 0.3 / i,
        blending: THREE.AdditiveBlending
      });
      const corona = new THREE.Mesh(coronaGeo, coronaMat);
      scene.add(corona);
    }

    // DETAILED PLANET DATA with textures
    const planetData = [
      { 
        name: 'Mercury', 
        radius: 1.8, 
        distance: 30, 
        color: 0xA0522D, 
        emissive: 0x8B4513,
        speed: 0.02, 
        rotationSpeed: 0.005,
        craters: true
      },
      { 
        name: 'Venus', 
        radius: 2.5, 
        distance: 45, 
        color: 0xFFD700, 
        emissive: 0xFFB347,
        speed: 0.015,
        rotationSpeed: 0.003,
        atmosphere: true
      },
      { 
        name: 'Earth', 
        radius: 2.8, 
        distance: 65, 
        color: 0x1E90FF, 
        emissive: 0x00CED1,
        speed: 0.01,
        rotationSpeed: 0.01,
        hasOceans: true,
        hasClouds: true,
        moons: [{ radius: 0.7, distance: 5, color: 0xE0E0E0 }]
      },
      { 
        name: 'Mars', 
        radius: 2.2, 
        distance: 90, 
        color: 0xFF4500, 
        emissive: 0xFF6347,
        speed: 0.008,
        rotationSpeed: 0.008,
        iceCaps: true
      },
      { 
        name: 'Jupiter', 
        radius: 7, 
        distance: 130, 
        color: 0xFFB347, 
        emissive: 0xFF8C00,
        speed: 0.005,
        rotationSpeed: 0.02,
        stripes: true,
        greatRedSpot: true
      },
      { 
        name: 'Saturn', 
        radius: 6.5, 
        distance: 170, 
        color: 0xF4D03F, 
        emissive: 0xDAA520,
        speed: 0.004,
        rotationSpeed: 0.015,
        hasRings: true
      },
      { 
        name: 'Uranus', 
        radius: 4, 
        distance: 210, 
        color: 0x40E0D0, 
        emissive: 0x00CED1,
        speed: 0.003,
        rotationSpeed: 0.01
      },
      { 
        name: 'Neptune', 
        radius: 3.8, 
        distance: 250, 
        color: 0x4169E1, 
        emissive: 0x1E90FF,
        speed: 0.002,
        rotationSpeed: 0.008
      }
    ];

    const planets = [];
    const orbitLines = [];

    planetData.forEach(data => {
      // Create detailed planet
      const planetGeometry = new THREE.SphereGeometry(data.radius, 128, 128);
      const planetMaterial = new THREE.MeshStandardMaterial({
        color: data.color,
        emissive: data.emissive,
        emissiveIntensity: 0.5,
        roughness: 0.4,
        metalness: 0.3
      });
      const planet = new THREE.Mesh(planetGeometry, planetMaterial);
      
      // Add planet glow
      const glowGeo = new THREE.SphereGeometry(data.radius * 1.15, 64, 64);
      const glowMat = new THREE.MeshBasicMaterial({
        color: data.emissive,
        transparent: true,
        opacity: 0.25,
        blending: THREE.AdditiveBlending
      });
      const glow = new THREE.Mesh(glowGeo, glowMat);
      planet.add(glow);

      // Earth clouds
      if (data.hasClouds) {
        const cloudGeo = new THREE.SphereGeometry(data.radius * 1.05, 64, 64);
        const cloudMat = new THREE.MeshBasicMaterial({
          color: 0xFFFFFF,
          transparent: true,
          opacity: 0.4,
          blending: THREE.AdditiveBlending
        });
        const clouds = new THREE.Mesh(cloudGeo, cloudMat);
        planet.add(clouds);
      }

      // Jupiter stripes and Great Red Spot
      if (data.stripes) {
        for (let i = 0; i < 5; i++) {
          const stripeGeo = new THREE.TorusGeometry(data.radius * 0.95, 0.3, 8, 64);
          const stripeMat = new THREE.MeshBasicMaterial({
            color: i % 2 === 0 ? 0xCD853F : 0x8B4513,
            transparent: true,
            opacity: 0.6
          });
          const stripe = new THREE.Mesh(stripeGeo, stripeMat);
          stripe.rotation.x = Math.PI / 2;
          stripe.position.y = (i - 2) * 1.5;
          planet.add(stripe);
        }
        
        // Great Red Spot
        const spotGeo = new THREE.CircleGeometry(1.5, 32);
        const spotMat = new THREE.MeshBasicMaterial({
          color: 0xFF0000,
          transparent: true,
          opacity: 0.8
        });
        const spot = new THREE.Mesh(spotGeo, spotMat);
        spot.position.set(0, -1, data.radius * 0.95);
        planet.add(spot);
      }

      // Saturn's detailed rings
      if (data.hasRings) {
        // Multiple ring layers
        const ringLayers = [
          { inner: data.radius * 1.5, outer: data.radius * 2.0, color: 0xFFE4B5, opacity: 0.9 },
          { inner: data.radius * 2.1, outer: data.radius * 2.5, color: 0xFFD700, opacity: 0.7 },
          { inner: data.radius * 2.6, outer: data.radius * 3.2, color: 0xF5DEB3, opacity: 0.6 },
          { inner: data.radius * 1.4, outer: data.radius * 1.45, color: 0xFFFFFF, opacity: 0.5 }
        ];
        
        ringLayers.forEach(layer => {
          const ringGeo = new THREE.RingGeometry(layer.inner, layer.outer, 256);
          const ringMat = new THREE.MeshBasicMaterial({
            color: layer.color,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: layer.opacity,
            emissive: 0xFFD700,
            emissiveIntensity: 0.3
          });
          const ring = new THREE.Mesh(ringGeo, ringMat);
          ring.rotation.x = Math.PI / 2.2;
          planet.add(ring);
        });
      }

      // Orbit container
      const orbitContainer = new THREE.Object3D();
      planet.position.x = data.distance;
      orbitContainer.add(planet);
      scene.add(orbitContainer);

      // Orbit line
      const orbitGeometry = new THREE.BufferGeometry();
      const orbitPoints = [];
      for (let i = 0; i <= 128; i++) {
        const angle = (i / 128) * Math.PI * 2;
        orbitPoints.push(
          Math.cos(angle) * data.distance,
          0,
          Math.sin(angle) * data.distance
        );
      }
      orbitGeometry.setAttribute('position', new THREE.Float32BufferAttribute(orbitPoints, 3));
      const orbitMaterial = new THREE.LineBasicMaterial({
        color: 0x555555,
        transparent: true,
        opacity: 0.3
      });
      const orbitLine = new THREE.Line(orbitGeometry, orbitMaterial);
      scene.add(orbitLine);

      // Planet label - LARGER AND MORE READABLE
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 1024;
      canvas.height = 256;
      
      // Add background for readability
      context.fillStyle = 'rgba(0, 0, 0, 0.7)';
      context.roundRect(100, 50, 824, 150, 20);
      context.fill();
      
      // Add border
      context.strokeStyle = 'rgba(255, 215, 0, 0.8)';
      context.lineWidth = 4;
      context.roundRect(100, 50, 824, 150, 20);
      context.stroke();
      
      // Text with shadow
      context.shadowColor = 'rgba(0, 0, 0, 0.8)';
      context.shadowBlur = 10;
      context.shadowOffsetX = 2;
      context.shadowOffsetY = 2;
      context.fillStyle = '#FFFFFF';
      context.font = 'Bold 80px Arial';
      context.textAlign = 'center';
      context.fillText(data.name, 512, 150);
      
      const texture = new THREE.CanvasTexture(canvas);
      const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true });
      const sprite = new THREE.Sprite(spriteMaterial);
      sprite.scale.set(20, 5, 1);
      sprite.position.y = data.radius + 6;
      planet.add(sprite);

      // Add moons
      if (data.moons) {
        data.moons.forEach(moonData => {
          const moonGeo = new THREE.SphereGeometry(moonData.radius, 32, 32);
          const moonMat = new THREE.MeshStandardMaterial({ color: moonData.color, roughness: 0.8 });
          const moon = new THREE.Mesh(moonGeo, moonMat);
          moon.position.x = moonData.distance;
          const moonOrbit = new THREE.Object3D();
          moonOrbit.add(moon);
          planet.add(moonOrbit);
          planets.push({ container: moonOrbit, speed: 0.03, mesh: moon, rotationSpeed: 0.01 });
        });
      }

      planets.push({
        container: orbitContainer,
        speed: data.speed,
        mesh: planet,
        rotationSpeed: data.rotationSpeed || 0.005
      });
    });

    // SATELLITES with blinking lights - MUCH BIGGER AND VISIBLE
    const satellites = [];
    for (let i = 0; i < 8; i++) {
      const satGroup = new THREE.Group();
      
      // Satellite body - 4X BIGGER
      const bodyGeo = new THREE.BoxGeometry(2, 1.2, 1.2);
      const bodyMat = new THREE.MeshStandardMaterial({ 
        color: 0xEEEEEE, 
        metalness: 1.0, 
        roughness: 0.1,
        emissive: 0x444444,
        emissiveIntensity: 0.3
      });
      const body = new THREE.Mesh(bodyGeo, bodyMat);
      satGroup.add(body);
      
      // Antenna
      const antennaGeo = new THREE.CylinderGeometry(0.1, 0.1, 2, 8);
      const antennaMat = new THREE.MeshStandardMaterial({ color: 0xCCCCCC, metalness: 0.9 });
      const antenna = new THREE.Mesh(antennaGeo, antennaMat);
      antenna.position.y = 1.5;
      satGroup.add(antenna);
      
      // Solar panels - MUCH BIGGER
      const panelGeo = new THREE.BoxGeometry(5, 0.15, 2.5);
      const panelMat = new THREE.MeshStandardMaterial({ 
        color: 0x000066, 
        metalness: 0.7, 
        emissive: 0x0000ff, 
        emissiveIntensity: 0.4 
      });
      const panel1 = new THREE.Mesh(panelGeo, panelMat);
      panel1.position.set(-2.5, 0, 0);
      satGroup.add(panel1);
      const panel2 = panel1.clone();
      panel2.position.set(2.5, 0, 0);
      satGroup.add(panel2);
      
      // BIGGER Blinking lights
      const lightColors = [0xFF0000, 0x00FF00, 0x0000FF, 0xFFFF00];
      const lights = [];
      lightColors.forEach((color, idx) => {
        const lightGeo = new THREE.SphereGeometry(0.25, 32, 32);
        const lightMat = new THREE.MeshBasicMaterial({ 
          color: color, 
          emissive: color, 
          emissiveIntensity: 1.5
        });
        const light = new THREE.Mesh(lightGeo, lightMat);
        light.position.set((idx - 1.5) * 0.7, 0.8, 0.6);
        satGroup.add(light);
        
        // Add glow effect
        const glowGeo = new THREE.SphereGeometry(0.35, 16, 16);
        const glowMat = new THREE.MeshBasicMaterial({
          color: color,
          transparent: true,
          opacity: 0.6,
          blending: THREE.AdditiveBlending
        });
        const glow = new THREE.Mesh(glowGeo, glowMat);
        light.add(glow);
        
        lights.push(light);
      });
      
      const distance = 70 + Math.random() * 150;
      const angle = (i / 8) * Math.PI * 2;
      satGroup.position.set(
        Math.cos(angle) * distance,
        Math.random() * 40 - 20,
        Math.sin(angle) * distance
      );
      
      scene.add(satGroup);
      satellites.push({ group: satGroup, lights: lights, speed: 0.001 + Math.random() * 0.002, angle: angle, distance: distance });
    }

    // COMETS
    const comets = [];
    const createComet = () => {
      const cometGroup = new THREE.Group();
      
      // Comet head
      const headGeo = new THREE.SphereGeometry(0.8, 32, 32);
      const headMat = new THREE.MeshBasicMaterial({ color: 0xCCFFFF, emissive: 0x00FFFF, emissiveIntensity: 0.8 });
      const head = new THREE.Mesh(headGeo, headMat);
      cometGroup.add(head);
      
      // Comet tail
      const tailGeo = new THREE.ConeGeometry(0.5, 20, 32);
      const tailMat = new THREE.MeshBasicMaterial({
        color: 0xAAFFFF,
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending
      });
      const tail = new THREE.Mesh(tailGeo, tailMat);
      tail.rotation.x = Math.PI / 2;
      tail.position.z = -10;
      cometGroup.add(tail);
      
      const startPos = new THREE.Vector3(
        (Math.random() - 0.5) * 500,
        (Math.random() - 0.5) * 200,
        -200
      );
      cometGroup.position.copy(startPos);
      
      const velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 3,
        (Math.random() - 0.5) * 2,
        5
      );
      
      scene.add(cometGroup);
      return { group: cometGroup, velocity: velocity, life: 300 };
    };

    // SUPERNOVA (triggers periodically)
    let supernovaTime = 0;
    let supernovaActive = false;
    let supernovaParticles = null;
    
    const createSupernova = () => {
      const particleCount = 5000;
      const geometry = new THREE.BufferGeometry();
      const positions = [];
      const colors = [];
      const velocities = [];
      
      for (let i = 0; i < particleCount; i++) {
        positions.push(0, 0, 0);
        const color = new THREE.Color();
        color.setHSL(Math.random() * 0.2, 1.0, 0.5);
        colors.push(color.r, color.g, color.b);
        
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.random() * Math.PI;
        const speed = 2 + Math.random() * 3;
        velocities.push(
          Math.sin(phi) * Math.cos(theta) * speed,
          Math.sin(phi) * Math.sin(theta) * speed,
          Math.cos(phi) * speed
        );
      }
      
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      
      const material = new THREE.PointsMaterial({
        size: 2,
        vertexColors: true,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending
      });
      
      const particles = new THREE.Points(geometry, material);
      particles.userData.velocities = velocities;
      particles.userData.age = 0;
      scene.add(particles);
      return particles;
    };

    // STAR FIELDS
    const createStarField = (count, size, color, distance) => {
      const geometry = new THREE.BufferGeometry();
      const positions = [];
      const colors = [];

      for (let i = 0; i < count; i++) {
        positions.push(
          (Math.random() - 0.5) * distance,
          (Math.random() - 0.5) * distance,
          (Math.random() - 0.5) * distance
        );
        const baseColor = new THREE.Color(color);
        const variation = 0.3;
        baseColor.r += (Math.random() - 0.5) * variation;
        baseColor.g += (Math.random() - 0.5) * variation;
        baseColor.b += (Math.random() - 0.5) * variation;
        colors.push(baseColor.r, baseColor.g, baseColor.b);
      }

      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

      const material = new THREE.PointsMaterial({
        size: size,
        vertexColors: true,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending
      });

      return new THREE.Points(geometry, material);
    };

    const starField1 = createStarField(8000, 0.8, 0xFFFFFF, 1500);
    const starField2 = createStarField(5000, 1.2, 0xFFDD88, 1200);
    const starField3 = createStarField(3000, 1.5, 0xFFAAAA, 1000);
    scene.add(starField1, starField2, starField3);

    // ANIMATION LOOP
    const animate = () => {
      requestAnimationFrame(animate);

      // Rotate sun with solar flares
      sun.rotation.y += 0.002;

      // Rotate planets
      planets.forEach(planet => {
        planet.container.rotation.y += planet.speed;
        planet.mesh.rotation.y += planet.rotationSpeed;
      });

      // Animate satellites with blinking lights
      satellites.forEach((sat, idx) => {
        sat.angle += sat.speed;
        sat.group.position.x = Math.cos(sat.angle) * sat.distance;
        sat.group.position.z = Math.sin(sat.angle) * sat.distance;
        sat.group.rotation.y += 0.01;
        
        // Blink lights
        const time = Date.now() * 0.001;
        sat.lights.forEach((light, i) => {
          light.material.emissiveIntensity = 0.5 + Math.sin(time * 5 + i + idx) * 0.5;
        });
      });

      // Spawn comets randomly
      if (Math.random() < 0.01 && comets.length < 3) {
        comets.push(createComet());
      }

      // Update comets
      for (let i = comets.length - 1; i >= 0; i--) {
        const comet = comets[i];
        comet.group.position.add(comet.velocity);
        comet.group.lookAt(comet.group.position.clone().add(comet.velocity));
        comet.life--;
        
        if (comet.life <= 0 || comet.group.position.z > 300) {
          scene.remove(comet.group);
          comets.splice(i, 1);
        }
      }

      // Supernova cycle (every 45 seconds)
      supernovaTime++;
      if (supernovaTime > 2700 && !supernovaActive) { // 45 seconds at 60fps
        supernovaActive = true;
        supernovaParticles = createSupernova();
        supernovaTime = 0;
      }

      // Update supernova
      if (supernovaActive && supernovaParticles) {
        supernovaParticles.userData.age++;
        const positions = supernovaParticles.geometry.attributes.position.array;
        const velocities = supernovaParticles.userData.velocities;
        
        for (let i = 0; i < positions.length; i += 3) {
          positions[i] += velocities[i];
          positions[i + 1] += velocities[i + 1];
          positions[i + 2] += velocities[i + 2];
        }
        
        supernovaParticles.geometry.attributes.position.needsUpdate = true;
        supernovaParticles.material.opacity = Math.max(0, 1 - supernovaParticles.userData.age / 180);
        
        if (supernovaParticles.userData.age > 180) {
          scene.remove(supernovaParticles);
          supernovaParticles = null;
          supernovaActive = false;
        }
      }

      // Slowly rotate camera
      const time = Date.now() * 0.00003;
      camera.position.x = Math.sin(time) * 250;
      camera.position.z = Math.cos(time) * 250;
      camera.position.y = 120 + Math.sin(time * 0.5) * 30;
      camera.lookAt(0, 0, 0);

      // Rotate star fields
      starField1.rotation.y += 0.0001;
      starField2.rotation.y -= 0.0002;
      starField3.rotation.x += 0.00015;

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize with debouncing
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      }, 100);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={mountRef}
      className="fixed inset-0 -z-10"
      style={{
        background: 'radial-gradient(ellipse at center, #0a0e27 0%, #000000 100%)'
      }}
    />
  );
};

export default RealisticGalaxyBackground;